import { IRunningOrder, IRunningOrderDogClass } from "@eagerdog/interfaces";

import { isBreak, isGapBreak } from "./labels";

export interface VisibleTile {
  /** Index in the judge's dog_classes array. */
  index: number;
  visible: boolean;
  /** Computed start, milliseconds. */
  startMs: number;
}

/**
 * Real dog_classes index to insert before. Dropping at or after the last
 * visible tile appends. Hidden placeholders stay put; the index counts only
 * visible tiles whose start is earlier than the drop.
 */
export function insertionIndexFromDrop(tiles: VisibleTile[], dropStartMs: number): number {
  const visible = tiles.filter((tile) => tile.visible).slice().sort((a, b) => a.index - b.index);
  let earlier = 0;
  for (const tile of visible) {
    if (tile.startMs < dropStartMs) {
      earlier += 1;
    } else {
      break;
    }
  }
  if (earlier >= visible.length) {
    return tiles.length > 0 ? Math.max(...tiles.map((tile) => tile.index)) + 1 : 0;
  }
  return visible[earlier].index;
}

export interface ClassMove {
  runningOrder: IRunningOrder[];
  moved: IRunningOrderDogClass;
  sourceJudgeIndex: number;
  destJudgeIndex: number;
  removedDuplicate: boolean;
}

function cloneOrder(runningOrder: IRunningOrder[]): IRunningOrder[] {
  return JSON.parse(JSON.stringify(runningOrder));
}

/**
 * Move one tile. Same-judge drops reorder. Cross-judge drops also drop a
 * zero-entry duplicate of the same element+level at the destination, matching
 * the Judge Schedules tab.
 */
export function moveClassTile(
  runningOrder: IRunningOrder[],
  sourceJudgeIndex: number,
  sourceClassIndex: number,
  destJudgeIndex: number,
  destInsertIndex: number
): ClassMove | undefined {
  const order = cloneOrder(runningOrder);
  const source = order[sourceJudgeIndex];
  const dest = order[destJudgeIndex];
  if (!source || !dest) {
    return undefined;
  }
  if (sourceClassIndex < 0 || sourceClassIndex >= source.dog_classes.length) {
    return undefined;
  }

  const [moved] = source.dog_classes.splice(sourceClassIndex, 1);
  let insertAt = destInsertIndex;
  if (sourceJudgeIndex === destJudgeIndex && sourceClassIndex < insertAt) {
    insertAt -= 1;
  }
  insertAt = Math.max(0, Math.min(insertAt, dest.dog_classes.length));

  if (sourceJudgeIndex === destJudgeIndex && insertAt === sourceClassIndex) {
    source.dog_classes.splice(sourceClassIndex, 0, moved);
    return { runningOrder: order, moved, sourceJudgeIndex, destJudgeIndex, removedDuplicate: false };
  }

  dest.dog_classes.splice(insertAt, 0, moved);

  let removedDuplicate = false;
  if (sourceJudgeIndex !== destJudgeIndex) {
    const dupeIndex = dest.dog_classes.findIndex((dogClass) =>
      dogClass !== moved &&
      dogClass.show_element === moved.show_element &&
      dogClass.level === moved.level &&
      (dogClass.entries || 0) === 0 &&
      dogClass.type !== "break"
    );
    if (dupeIndex !== -1) {
      dest.dog_classes.splice(dupeIndex, 1);
      removedDuplicate = true;
    }
  }

  return { runningOrder: order, moved, sourceJudgeIndex, destJudgeIndex, removedDuplicate };
}

function isGroupedChild(parent: IRunningOrderDogClass, child: IRunningOrderDogClass): boolean {
  if (!child.group_with || isBreak(child)) {
    return false;
  }
  const parentKey = `${parent.show_element}:${parent.level}`;
  return child.group_with === parentKey || (child.group_with === parent.level && child.show_element === parent.show_element);
}

/**
 * Drop a class or break bar on another row of the same judge.
 * The dragged tile takes that row's place. A hidden gap stays in front of the
 * tile it was delaying, and grouped children stay with their parent.
 */
export function reorderClassToRow(
  runningOrder: IRunningOrder[],
  judgeIndex: number,
  fromIndex: number,
  toIndex: number
): IRunningOrder[] | undefined {
  const order = cloneOrder(runningOrder);
  const classes = order[judgeIndex]?.dog_classes;
  if (!classes || fromIndex === toIndex) {
    return undefined;
  }
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= classes.length || toIndex >= classes.length) {
    return undefined;
  }

  const consumed = new Set<number>();
  const blocks: number[][] = [];
  classes.forEach((dogClass, index) => {
    if (consumed.has(index) || dogClass.group_with || isGapBreak(dogClass)) {
      return;
    }
    const pieces: number[] = [];
    if (index > 0 && isGapBreak(classes[index - 1]) && !consumed.has(index - 1)) {
      pieces.push(index - 1);
      consumed.add(index - 1);
    }
    pieces.push(index);
    consumed.add(index);
    classes.forEach((other, otherIndex) => {
      if (!consumed.has(otherIndex) && isGroupedChild(dogClass, other)) {
        pieces.push(otherIndex);
        consumed.add(otherIndex);
      }
    });
    blocks.push(pieces);
  });
  classes.forEach((_, index) => {
    if (!consumed.has(index)) {
      blocks.push([index]);
    }
  });

  const fromBlock = blocks.findIndex((pieces) => pieces.includes(fromIndex));
  const toBlock = blocks.findIndex((pieces) => pieces.includes(toIndex));
  if (fromBlock < 0 || toBlock < 0 || fromBlock === toBlock) {
    return undefined;
  }
  const [moved] = blocks.splice(fromBlock, 1);
  blocks.splice(toBlock, 0, moved);
  order[judgeIndex].dog_classes = blocks.flat().map((index) => classes[index]);
  return order;
}

export function reorderJudges(runningOrder: IRunningOrder[], fromIndex: number, toIndex: number): IRunningOrder[] {
  const order = cloneOrder(runningOrder);
  if (fromIndex < 0 || fromIndex >= order.length || toIndex < 0 || toIndex >= order.length || fromIndex === toIndex) {
    return order;
  }
  const [judge] = order.splice(fromIndex, 1);
  order.splice(toIndex, 0, judge);
  return order;
}
